import { useEffect } from "react";

const PRODUCTION_LP_ORIGIN = "https://priorityonewashclub.com";

const LP_PARENT_ORIGINS = new Set([
  "https://priorityonewashclub.com",
  "https://www.priorityonewashclub.com",
  "http://localhost:3000",
  "http://localhost:3002",
  "http://127.0.0.1:3000",
  "http://127.0.0.1:3002",
]);

export function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function isAllowlistedOrigin(origin) {
  if (!origin) return false;
  if (LP_PARENT_ORIGINS.has(origin)) return true;
  try {
    const { hostname, protocol } = new URL(origin);
    if (protocol === "http:" && (hostname === "localhost" || hostname === "127.0.0.1")) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * Prefer document.referrer when it matches the LP allowlist; otherwise production LP origin.
 */
export function getLpParentOrigin() {
  try {
    if (document.referrer) {
      const referrerOrigin = new URL(document.referrer).origin;
      if (isAllowlistedOrigin(referrerOrigin)) {
        return referrerOrigin;
      }
    }
  } catch {
    // fall through
  }
  return PRODUCTION_LP_ORIGIN;
}

export function postToLpParent(message) {
  if (!isInIframe()) return;
  try {
    window.parent.postMessage(message, getLpParentOrigin());
  } catch (err) {
    console.warn("[iframeEmbed] postMessage failed", err);
  }
}

export function redirectToStripeCheckout(checkoutUrl) {
  if (!checkoutUrl) return;
  if (isInIframe()) {
    postToLpParent({ type: "stripe-checkout", url: checkoutUrl });
    return;
  }
  window.location.href = checkoutUrl;
}

function measureContentHeight() {
  const doc = document.documentElement;
  const body = document.body;
  return Math.max(
    doc?.scrollHeight || 0,
    doc?.offsetHeight || 0,
    body?.scrollHeight || 0,
    body?.offsetHeight || 0
  );
}

/**
 * Posts { type: 'iframe-resize', height } to the LP parent while enabled.
 */
export function useIframeAutoResize(enabled) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    let lastHeight = 0;

    const publishHeight = () => {
      const height = measureContentHeight();
      if (!height || height === lastHeight) return;
      lastHeight = height;
      postToLpParent({ type: "iframe-resize", height });
    };

    publishHeight();

    const timeouts = [50, 200, 500, 1000].map((ms) =>
      window.setTimeout(publishHeight, ms)
    );

    window.addEventListener("resize", publishHeight);

    let observer;
    if (typeof ResizeObserver !== "undefined" && document.body) {
      observer = new ResizeObserver(publishHeight);
      observer.observe(document.body);
      if (document.documentElement) {
        observer.observe(document.documentElement);
      }
    }

    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", publishHeight);
      observer?.disconnect();
    };
  }, [enabled]);
}
